import * as yup from 'yup';

import { requiredString } from 'utils/constants';

export const FormFields = {
  patientAccountNumber: 'patientAccountNumber',
  placerOrder: 'placerOrder',
  fillerOrder: 'fillerOrder',
  placerGroup: 'placerGroup',
  orderStatus: 'orderStatus',
  quantity: 'quantity',
  dtOfTrnsaction: 'dtOfTrnsaction',
  orderingStaffText: 'orderingStaffText',
  orderLocation: 'orderLocation',
  testCodeId: 'testCodeId',
  testCodeText: 'testCodeText',
  placerField: 'placerField',
  fillerField: 'fillerField',
  diagService: 'diagService',
  orderingStaffId: 'orderingStaffId',
};

export const schema = yup.object().shape({
  // Required fields
  [FormFields.patientAccountNumber]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `Patient Account Number should not exceed ${max} characters`),
  [FormFields.testCodeId]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Test Code ID should not exceed ${max} characters`),
  [FormFields.testCodeText]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Test Code Text should not exceed ${max} characters`),
  [FormFields.placerOrder]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `Placer Order should not exceed ${max} characters`),
  // Optional fields
  [FormFields.fillerOrder]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Filler Order should not exceed ${max} characters`),
  [FormFields.placerGroup]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Placer Group should not exceed ${max} characters`),
  [FormFields.orderStatus]: yup
    .string()
    .trim()
    .max(50, ({ max }) => `Order Status should not exceed ${max} characters`),
  [FormFields.quantity]: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .typeError('Quantity must be a valid number')
    .integer('Quantity must be a whole number')
    .min(0, 'Quantity must be a positive number'),
  [FormFields.dtOfTrnsaction]: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Invalid date of transaction'),
  [FormFields.orderingStaffText]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Ordering Staff Text should not exceed ${max} characters`),
  [FormFields.orderLocation]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Order Location should not exceed ${max} characters`),
  [FormFields.placerField]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Placer Field should not exceed ${max} characters`),
  [FormFields.fillerField]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Filler Field should not exceed ${max} characters`),
  [FormFields.diagService]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Diag Service should not exceed ${max} characters`),
  [FormFields.orderingStaffId]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Ordering Staff ID should not exceed ${max} characters`),
});

export const mapApi2Form = (apiData) => {
  if (!apiData || Object.keys(apiData).length === 0) return {};

  return {
    id: apiData.id,
    [FormFields.patientAccountNumber]: apiData.patientAccountNumber || '',
    [FormFields.placerOrder]: apiData.placerOrder || '',
    [FormFields.fillerOrder]: apiData.fillerOrder || '',
    [FormFields.placerGroup]: apiData.placerGroup || '',
    [FormFields.orderStatus]: apiData.orderStatus || '',
    [FormFields.quantity]: apiData.quantity ?? '',
    [FormFields.dtOfTrnsaction]: apiData.dtOfTrnsaction || '',
    [FormFields.orderingStaffText]: apiData.orderingStaffText || '',
    [FormFields.orderLocation]: apiData.orderLocation || '',
    [FormFields.testCodeId]: apiData.testCodeId || '',
    [FormFields.testCodeText]: apiData.testCodeText || '',
    [FormFields.placerField]: apiData.placerField || '',
    [FormFields.fillerField]: apiData.fillerField || '',
    [FormFields.diagService]: apiData.diagService || '',
    [FormFields.orderingStaffId]: apiData.orderingStaffId || '',
  };
};

export const mapForm2Api = (formData) => {
  const payload = {};

  // Helper to add non-empty values
  const addIfNotEmpty = (key, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (trimmedValue !== null && trimmedValue !== undefined && trimmedValue !== '') {
      payload[key] = trimmedValue;
    }
  };

  // Include id for updates
  if (formData.id) {
    payload.id = formData.id;
  }

  // Add all fields only if they have values
  addIfNotEmpty('patientAccountNumber', formData[FormFields.patientAccountNumber]);
  addIfNotEmpty('placerOrder', formData[FormFields.placerOrder]);
  addIfNotEmpty('fillerOrder', formData[FormFields.fillerOrder]);
  addIfNotEmpty('placerGroup', formData[FormFields.placerGroup]);
  addIfNotEmpty('orderStatus', formData[FormFields.orderStatus]);
  addIfNotEmpty('quantity', formData[FormFields.quantity]);
  addIfNotEmpty('dtOfTrnsaction', formData[FormFields.dtOfTrnsaction]);
  addIfNotEmpty('orderingStaffText', formData[FormFields.orderingStaffText]);
  addIfNotEmpty('orderLocation', formData[FormFields.orderLocation]);
  addIfNotEmpty('testCodeId', formData[FormFields.testCodeId]);
  addIfNotEmpty('testCodeText', formData[FormFields.testCodeText]);
  addIfNotEmpty('placerField', formData[FormFields.placerField]);
  addIfNotEmpty('fillerField', formData[FormFields.fillerField]);
  addIfNotEmpty('diagService', formData[FormFields.diagService]);
  addIfNotEmpty('orderingStaffId', formData[FormFields.orderingStaffId]);

  return payload;
};
